import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MessageIcon from "@mui/icons-material/Message";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch } from "react-redux";
import { getAuthToken } from "../../utils/getAuthToken";
import { addNodeToBranch, updateNodeInBranch } from "../../redux/Slice/Workflow/workflowSlice";
import axios from "axios";
import ReactTimezoneSelect from "react-timezone-select";
import { buildTimezoneSelectStyles } from "./timezoneSelectStyles";

const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const WEBSITE_COLOR = "#1e7d4d";
const WEBSITE_COLOR_HOVER = "#175f3a";

// Website Chat triggers — same shape as Instagram/Facebook's own trigger catalog (see
// ChooseInstagramTriggerDrawer.jsx). Trigger names must match what workflow.triggers.js's
// handleIncomingMessageWorkflow/handleCloseConversationWorkflow look for (channel: 'website').
const WEBSITE_TRIGGERS = [
  {
    id: "first-incoming-website-chat-message",
    name: "First incoming website chat message",
    description:
      "When a visitor sends their first message in your website chat widget. You can add a condition (e.g. keywords) and set an expiry time, same as WhatsApp's Contact sends first incoming message.",
    triggerType: "root",
    category: "Message",
    config: [
      {
        name: "Conditions",
        fields: [
          {
            label: "Trigger when",
            type: "select",
            options: ["Any first message", "When message contains keywords"],
          },
          {
            label: "Keywords (comma-separated)",
            type: "text",
            description: "Optional. Only trigger when the first message contains one of these keywords.",
          },
        ],
      },
      // Read server-side by workflow.expiry.js's handleExpiry (labels must match exactly)
      {
        name: "Select Time Interval",
        fields: [
          {
            label: "Select Interval Duration",
            type: "number",
            selectedValue: 1,
            description: "Workflow expires after this duration if no reply (0 = no expiry).",
          },
          {
            label: "Select Duration Type",
            type: "select",
            options: ["Minutes", "Hours", "Days"],
            selectedValue: "Days",
          },
          {
            label: "Timezone",
            type: "select",
            options: ["timezones"],
            description: "Timezone for expiry.",
          },
        ],
      },
    ],
  },
  {
    id: "when-contact-closed",
    name: "When contact is closed",
    description: "When a teammate closes a website chat conversation (marks it as done). Runs the workflow when the chat is closed.",
    triggerType: "root",
    category: "Contact",
    config: [],
  },
];

const isTimezoneField = (field) => Array.isArray(field.options) && field.options[0] === "timezones";
const defaultTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

// Form state for a trigger's config: saved/default values where present, otherwise the first
// select option, the browser's timezone, 0 for numbers, or "".
function buildInitialFormValues(config) {
  const values = {};
  (config || []).forEach((cfg) => {
    (cfg.fields || []).forEach((field) => {
      const saved = field.selectedValue;
      if (isTimezoneField(field)) {
        values[field.label] = typeof saved === "string" && saved ? saved : saved?.value ?? defaultTimezone();
      } else if (field.type === "number") {
        values[field.label] = saved ?? 0;
      } else if (saved !== undefined && saved !== null && saved !== "") {
        values[field.label] = saved;
      } else if (field.type === "select" && Array.isArray(field.options) && field.options.length > 0) {
        values[field.label] = field.options[0];
      } else {
        values[field.label] = "";
      }
    });
  });
  return values;
}

const TRIGGER_ICONS = {
  "First incoming website chat message": <ChatBubbleOutlineIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />,
  "When contact is closed": <CheckCircleIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />,
};

const WEBSITE_CATEGORY_ICONS = {
  Message: <MessageIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />,
  Contact: <PersonIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />,
};

/**
 * Workflows saved before new trigger fields existed only store the old config shape —
 * merge the saved values back onto the canonical WEBSITE_TRIGGERS entry so editing an
 * existing trigger still shows every current field. Same idea as Instagram's own
 * mergeInstagramTriggerWithCanonical.
 */
function mergeWebsiteTriggerWithCanonical(savedTrigger, triggers = WEBSITE_TRIGGERS) {
  if (!savedTrigger?.name) return savedTrigger;
  const canonical = triggers.find((t) => t.name === savedTrigger.name);
  if (!canonical || !Array.isArray(canonical.config)) return savedTrigger;
  const savedConfig = Array.isArray(savedTrigger.config) ? savedTrigger.config : [];
  const mergedConfig = canonical.config.map((templateCfg) => {
    const savedCfg = savedConfig.find((c) => c.name === templateCfg.name);
    const mergedFields = (templateCfg.fields || []).map((templateField) => {
      const savedField = savedCfg?.fields?.find((f) => f.label === templateField.label);
      if (savedField && savedField.selectedValue !== undefined && savedField.selectedValue !== null) {
        return { ...templateField, selectedValue: savedField.selectedValue };
      }
      return { ...templateField };
    });
    return { ...templateCfg, fields: mergedFields };
  });
  return { ...savedTrigger, ...canonical, config: mergedConfig };
}

// `triggers` / `title` let another channel with the same kind of triggers (WhatsApp Web) reuse this drawer unchanged.
const ChooseWebsiteTriggerDrawer = ({ open, onClose, editTrigger, branchId = "start", triggers = WEBSITE_TRIGGERS, title = "Choose a Website Chat trigger" }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState(null);

  const triggerMuiMenuProps = {
    disablePortal: false,
    PaperProps: { sx: { zIndex: (t) => t.zIndex.modal + 2, bgcolor: isDark ? "#2d3136" : "#fff", maxHeight: 320 } },
  };
  const triggerMuiSelectSx = {
    mt: 0.5,
    borderRadius: "6px",
    fontSize: 15,
    bgcolor: isDark ? "#23272b" : "#fff",
    color: isDark ? "#fff" : "#222",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#444" : "#ccc" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: isDark ? "#666" : "#999" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" },
  };

  const handleAccordionChange = (category) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? category : null);
  };

  const triggersByCategory = React.useMemo(() => {
    const map = {};
    triggers.forEach((t) => {
      const cat = t.category || "Message";
      if (!map[cat]) map[cat] = [];
      map[cat].push(t);
    });
    return map;
  }, [triggers]);

  useEffect(() => {
    if (open && editTrigger) {
      const merged = mergeWebsiteTriggerWithCanonical(editTrigger, triggers);
      setSelectedTrigger(merged);
      setFormValues(buildInitialFormValues(merged.config));
    } else if (!open) {
      setSelectedTrigger(null);
      setFormValues({});
    }
  }, [open, editTrigger, triggers]);

  const handleSaveTrigger = async () => {
    if (!selectedTrigger) return;

    const configWithSelectedValues = (selectedTrigger.config || []).map((cfg) => ({
      ...cfg,
      fields: (cfg.fields || []).map((field) => {
        const value = formValues[field.label];
        let selectedValue = value !== undefined ? value : "";
        if (field.type === "number") {
          const num = parseInt(value, 10);
          selectedValue = Number.isNaN(num) ? 0 : Math.max(num, 0);
        } else if (isTimezoneField(field)) {
          selectedValue = typeof value === "object" && value?.value ? value.value : value || "UTC";
        }
        return { ...field, selectedValue };
      }),
    }));

    const triggerWithConfig = { ...selectedTrigger, config: configWithSelectedValues };

    setSaving(true);

    if (editTrigger && editTrigger.branchId && editTrigger.nodeId !== undefined && editTrigger.nodeId !== null) {
      await dispatch(
        updateNodeInBranch({ branchId: editTrigger.branchId, nodeId: editTrigger.nodeId, updates: { trigger: triggerWithConfig } })
      );
    } else if (branchId) {
      const node = { id: Date.now().toString(), type: "trigger", trigger: triggerWithConfig };
      await dispatch(addNodeToBranch({ branchId, node, nodeIndex: 0 }));
    } else {
      setSaving(false);
      return;
    }

    const finalState = window.store?.getState ? window.store.getState() : null;
    const finalWorkflow = finalState?.workflow?.selectedWorkflow;

    if (finalWorkflow && finalWorkflow._id) {
      const token = await getAuthToken();
      if (!token) {
        setSaving(false);
        return;
      }
      try {
        await axios.put(`${SERVER_DOMAIN}/api/workflow/update/${finalWorkflow._id}`, finalWorkflow, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        });
        setSaving(false);
        if (onClose) onClose();
      } catch (err) {
        console.error("Backend update failed", err);
        setSaving(false);
      }
    } else {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "absolute", top: 0, right: 0, height: "100%", width: 360,
        bgcolor: isDark ? "#23272b" : "#fff", boxShadow: 6, zIndex: 30,
        borderLeft: `1.5px solid ${isDark ? "#444" : "#e0e0e0"}`, p: 1,
        display: "flex", flexDirection: "column", color: isDark ? theme.palette.text.primary : "inherit",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {selectedTrigger && (
            <IconButton onClick={() => setSelectedTrigger(null)} sx={{ mr: 1, color: isDark ? "#fff" : "#222" }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <FlashOnIcon sx={{ color: WEBSITE_COLOR, fontSize: 28, mr: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: isDark ? theme.palette.text.primary : undefined }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          mt: 2, color: isDark ? theme.palette.text.secondary : "text.secondary", flex: 1, overflowY: "auto",
          pb: selectedTrigger ? 8 : 0, p: 1,
          scrollbarWidth: { xs: "none", sm: "auto" }, msOverflowStyle: { xs: "none", sm: "auto" },
          "&::-webkit-scrollbar": { width: { xs: 0, sm: 8 }, height: { xs: 0, sm: 8 } },
        }}
      >
        {selectedTrigger ? (
          <Box sx={{ position: "relative", minHeight: 300, pb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, color: isDark ? "#fff" : "#222" }}>
              {selectedTrigger.name}
            </Typography>

            {Array.isArray(selectedTrigger.config) && selectedTrigger.config.length > 0 ? (
              selectedTrigger.config.map((cfg, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  <Typography sx={{ fontWeight: 600, mb: 1, color: isDark ? "#fff" : "#222" }}>{cfg.name}</Typography>
                  {Array.isArray(cfg.fields) &&
                    cfg.fields.map((field, fidx) => {
                      // Keywords only shown when "Trigger when" = "When message contains keywords"
                      const isKeywordField = field.label === "Keywords (comma-separated)";
                      const triggerWhen = formValues["Trigger when"];
                      const showKeywordField = !isKeywordField || triggerWhen === "When message contains keywords";
                      if (!showKeywordField) return null;
                      return (
                        <Box key={fidx} sx={{ mb: 2 }}>
                          <label style={{ color: isDark ? "#bbb" : "#555", fontSize: 14 }}>{field.label}</label>
                          {field.description && (
                            <Typography variant="caption" display="block" sx={{ color: isDark ? "#888" : "#666", mb: 0.5 }}>
                              {field.description}
                            </Typography>
                          )}
                          {isTimezoneField(field) ? (
                            <Box sx={{ mt: 1, mb: 2, width: "90%" }}>
                              <ReactTimezoneSelect
                                value={
                                  typeof formValues[field.label] === "object" && formValues[field.label]?.value
                                    ? formValues[field.label]
                                    : formValues[field.label] || defaultTimezone()
                                }
                                onChange={(tz) => setFormValues((v) => ({ ...v, [field.label]: tz }))}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={buildTimezoneSelectStyles(isDark, {
                                  controlAndMenuWidth: "90%",
                                  menuPortalZIndex: 9999,
                                  accentColor: WEBSITE_COLOR,
                                })}
                              />
                            </Box>
                          ) : field.type === "select" && Array.isArray(field.options) && field.options.length > 0 ? (
                            <FormControl size="small" sx={{ mt: 1, mb: 1, width: "90%", maxWidth: "100%" }}>
                              <Select
                                value={field.options.includes(formValues[field.label]) ? formValues[field.label] : field.options[0] || ""}
                                onChange={(e) => setFormValues((v) => ({ ...v, [field.label]: e.target.value }))}
                                MenuProps={triggerMuiMenuProps}
                                sx={triggerMuiSelectSx}
                              >
                                {field.options.map((opt, i) => (
                                  <MenuItem key={i} value={opt} sx={{ fontSize: 15 }}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <input
                              type={field.type === "number" ? "number" : "text"}
                              min={field.type === "number" ? 0 : undefined}
                              value={formValues[field.label] ?? ""}
                              onChange={(e) => setFormValues((v) => ({ ...v, [field.label]: e.target.value }))}
                              placeholder={field.description || ""}
                              style={{
                                width: "90%", marginTop: 4, padding: "8px 10px", borderRadius: 6,
                                border: `1px solid ${isDark ? "#444" : "#ccc"}`,
                                background: isDark ? "#23272b" : "#fff", color: isDark ? "#fff" : "#222",
                                fontSize: 15, marginBottom: 8,
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                </Box>
              ))
            ) : (
              <Typography sx={{ color: isDark ? "#aaa" : "#666", fontSize: 14 }}>
                No additional settings. Save to use this trigger.
              </Typography>
            )}

            <Box
              sx={{
                position: "relative", width: "100%", zIndex: 40, px: 2,
                background: isDark ? "#23272b" : "#fff", borderTop: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                display: "flex", justifyContent: "flex-start", py: 2, mt: 3,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  borderRadius: "20px", backgroundColor: WEBSITE_COLOR, color: "white", textTransform: "capitalize",
                  padding: "8px 16px", fontWeight: 600, fontSize: 16, boxShadow: 2,
                  "&:hover": { backgroundColor: WEBSITE_COLOR_HOVER },
                }}
                onClick={handleSaveTrigger}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Box>
        ) : (
          ["Message", "Contact"].map((category) => {
            const triggers = triggersByCategory[category] || [];
            if (triggers.length === 0) return null;
            return (
              <Accordion
                key={category}
                expanded={expandedAccordion === category}
                onChange={handleAccordionChange(category)}
                sx={{
                  bgcolor: isDark ? "#23272b" : "#f5f5f5", color: theme.palette.text.primary, mb: 1, boxShadow: 0,
                  border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {WEBSITE_CATEGORY_ICONS[category] || <FlashOnIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />}
                    <Typography fontWeight={600}>{category}</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0 }}>
                  {triggers.map((trigger) => (
                    <Paper
                      key={trigger.id}
                      elevation={isDark ? 2 : 1}
                      sx={{
                        mb: 2, p: 2, bgcolor: isDark ? "#23272b" : "#fff",
                        border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
                        transition: "box-shadow 0.2s, border-color 0.2s", cursor: "pointer",
                        "&:hover": { boxShadow: 4, borderColor: WEBSITE_COLOR, background: isDark ? "#1a3a2a" : "#e6f4ec" },
                      }}
                      onClick={() => {
                        setSelectedTrigger(trigger);
                        setFormValues(buildInitialFormValues(trigger.config));
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                        {TRIGGER_ICONS[trigger.name] || <FlashOnIcon sx={{ mr: 1, color: WEBSITE_COLOR }} />}
                        <Box>
                          <Typography fontWeight={700} fontSize={16} gutterBottom>
                            {trigger.name}
                          </Typography>
                          <Typography fontSize={14} color={isDark ? theme.palette.text.secondary : "text.secondary"}>
                            {trigger.description || "No description"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default ChooseWebsiteTriggerDrawer;
